import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { buildPulseContext, type PulseContext, type PulseEntityType } from "@/hooks/usePulse";
import { usePulseUI } from "@/contexts/PulseContext";

interface DerivedEntity {
  entityType: PulseEntityType;
  entityId: string | null;
}

function deriveEntity(pathname: string, params: Record<string, string | undefined>): DerivedEntity {
  if (pathname.startsWith("/app/players/") && params.id) {
    return { entityType: "player", entityId: params.id };
  }
  if (pathname.startsWith("/app/matches/") && (params.matchId || params.id)) {
    return { entityType: "match", entityId: params.matchId ?? params.id ?? null };
  }
  if (pathname.startsWith("/app/rtp/") && params.id) {
    return { entityType: "player", entityId: params.id };
  }
  if (pathname.startsWith("/app/planned-sessions/") && (params.sessionId || params.id)) {
    return { entityType: "session", entityId: params.sessionId ?? params.id ?? null };
  }
  return { entityType: null, entityId: null };
}

/**
 * Derives the current Pulse context from the route + organization,
 * and pushes it into the PulseUI context for the panel to consume.
 */
export function usePulseContextSync() {
  const { pathname } = useLocation();
  const params = useParams();
  const { currentOrganization, currentRole } = useOrganization();
  const { setCurrentContext } = usePulseUI();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id ?? "";
      const orgId = currentOrganization?.id ?? "";
      const role = currentRole ?? "";
      const { entityType, entityId } = deriveEntity(pathname, params);

      const ctx: PulseContext = buildPulseContext(
        pathname,
        entityType,
        entityId,
        null, // entityName resolved server-side
        orgId,
        role,
        userId,
      );
      if (!cancelled) setCurrentContext(ctx);
    })();
    return () => {
      cancelled = true;
    };
  }, [pathname, params, currentOrganization?.id, currentRole, setCurrentContext]);
}

export function buildContextSnapshot(ctx: PulseContext | null) {
  if (!ctx) return null;
  return { entityName: ctx.entityName, entityType: ctx.entityType };
}
